/* ─────────────────────────────────────────────────────────────────────
   Tykables 7.0 — Native Feel (progressive enhancement)

   Runs on DOMContentLoaded and on any subsequent DOM mutations (cart
   drawer re-renders, predictive search, quick-view injections). Purely
   additive: only sets attributes/hints that aren't already present.

   Covers:
     • inputmode + enterkeyhint on form inputs (search, email, tel, qty,
       login, contact, newsletter)
     • autocomplete hints where missing
     • decoding="async" on all <img> without it
     • fetchpriority="high" on LCP-ish hero images
   ───────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  // ── 1) Image decoding — async everywhere it isn't set ───────────────
  function enhanceImages(root) {
    const imgs = (root || document).querySelectorAll('img:not([decoding])');
    for (let i = 0; i < imgs.length; i++) {
      imgs[i].setAttribute('decoding', 'async');
    }
  }

  // ── 2) Input mode + enter-key hints + autocomplete ──────────────────
  // Maps matcher → { inputmode, enterkeyhint, autocomplete }
  // Only sets values that aren't already on the element.
  function enhanceInputs(root) {
    const scope = root || document;

    // Search fields
    scope.querySelectorAll('input[type="search"], input[name="q"]').forEach(function (el) {
      set(el, 'enterkeyhint', 'search');
      set(el, 'autocomplete', 'off');
      set(el, 'autocorrect', 'off');
      set(el, 'autocapitalize', 'off');
      set(el, 'spellcheck', 'false');
    });

    // Email fields
    scope.querySelectorAll('input[type="email"]').forEach(function (el) {
      set(el, 'inputmode', 'email');
      set(el, 'autocomplete', 'email');
      set(el, 'autocapitalize', 'off');
      set(el, 'autocorrect', 'off');
      set(el, 'spellcheck', 'false');
      // Newsletter signup forms → send. Everything else → next.
      const inNewsletter = el.closest('.newsletter-form, [data-newsletter], .email-signup');
      set(el, 'enterkeyhint', inNewsletter ? 'send' : 'next');
    });

    // Telephone fields
    scope.querySelectorAll('input[type="tel"]').forEach(function (el) {
      set(el, 'inputmode', 'tel');
      set(el, 'autocomplete', 'tel');
      set(el, 'enterkeyhint', 'next');
    });

    // Numeric / quantity fields
    scope.querySelectorAll('input[type="number"]').forEach(function (el) {
      set(el, 'inputmode', 'numeric');
      set(el, 'enterkeyhint', 'done');
      // Quantity input pattern — digits only
      if (el.classList.contains('quantity__input') || el.name && /updates\[/.test(el.name)) {
        set(el, 'pattern', '[0-9]*');
      }
    });

    // Password fields — usually next (login → submit password next); final
    // field in a form can be 'go' but we'd have to detect position, keep 'next'.
    scope.querySelectorAll('input[type="password"]').forEach(function (el) {
      set(el, 'enterkeyhint', 'go');
      if (el.name && /new/i.test(el.name)) {
        set(el, 'autocomplete', 'new-password');
      } else {
        set(el, 'autocomplete', 'current-password');
      }
    });

    // Text fields — sniff common name/id patterns
    scope.querySelectorAll('input[type="text"]').forEach(function (el) {
      const key = ((el.name || '') + ' ' + (el.id || '') + ' ' + (el.autocomplete || '')).toLowerCase();

      if (/first[-_ ]?name|firstname|given[-_ ]?name/.test(key)) {
        set(el, 'autocomplete', 'given-name');
        set(el, 'autocapitalize', 'words');
        set(el, 'enterkeyhint', 'next');
      } else if (/last[-_ ]?name|lastname|family[-_ ]?name|surname/.test(key)) {
        set(el, 'autocomplete', 'family-name');
        set(el, 'autocapitalize', 'words');
        set(el, 'enterkeyhint', 'next');
      } else if (/address[-_ ]?1|street|line1/.test(key)) {
        set(el, 'autocomplete', 'address-line1');
        set(el, 'autocapitalize', 'words');
        set(el, 'enterkeyhint', 'next');
      } else if (/address[-_ ]?2|apt|suite|line2/.test(key)) {
        set(el, 'autocomplete', 'address-line2');
        set(el, 'autocapitalize', 'words');
        set(el, 'enterkeyhint', 'next');
      } else if (/city|town/.test(key)) {
        set(el, 'autocomplete', 'address-level2');
        set(el, 'autocapitalize', 'words');
        set(el, 'enterkeyhint', 'next');
      } else if (/state|province|region/.test(key)) {
        set(el, 'autocomplete', 'address-level1');
        set(el, 'autocapitalize', 'words');
        set(el, 'enterkeyhint', 'next');
      } else if (/zip|post(al)?[-_ ]?code/.test(key)) {
        set(el, 'autocomplete', 'postal-code');
        set(el, 'inputmode', 'numeric');
        set(el, 'enterkeyhint', 'next');
      } else if (/country/.test(key)) {
        set(el, 'autocomplete', 'country-name');
        set(el, 'enterkeyhint', 'next');
      } else if (/discount|promo|code/.test(key)) {
        set(el, 'autocapitalize', 'characters');
        set(el, 'autocorrect', 'off');
        set(el, 'spellcheck', 'false');
        set(el, 'enterkeyhint', 'done');
      }
    });

    // Textareas — message/body likely a 'send' or 'done' depending on form
    scope.querySelectorAll('textarea').forEach(function (el) {
      const inContact = el.closest('form[action*="/contact"], .contact-form, [data-contact-form]');
      set(el, 'enterkeyhint', inContact ? 'send' : 'done');
    });
  }

  // ── Helpers ─────────────────────────────────────────────────────────
  function set(el, attr, value) {
    if (!el.hasAttribute(attr) || el.getAttribute(attr) === '') {
      el.setAttribute(attr, value);
    }
  }

  function enhance(root) {
    try {
      enhanceImages(root);
      enhanceInputs(root);
    } catch (e) {
      // Never break the page for a progressive enhancement.
      if (window.console && console.warn) console.warn('tykables-native:', e);
    }
  }

  // ── Kickoff ─────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { enhance(document); });
  } else {
    enhance(document);
  }

  // Re-run after dynamic injections (cart drawer, predictive search, quick view).
  // Uses a single debounced MutationObserver across <body> for low overhead.
  if ('MutationObserver' in window) {
    let pending = false;
    const observer = new MutationObserver(function (mutations) {
      if (pending) return;
      // Only bother if a mutation added element nodes.
      let touched = false;
      for (let i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          for (let j = 0; j < mutations[i].addedNodes.length; j++) {
            if (mutations[i].addedNodes[j].nodeType === 1) { touched = true; break; }
          }
        }
        if (touched) break;
      }
      if (!touched) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        enhance(document);
      });
    });

    const start = function () {
      if (document.body) observer.observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) start();
    else document.addEventListener('DOMContentLoaded', start);
  }
})();
